import React from 'react';
import { Component } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';

import { Theme } from '../Theme';
import { createGuideMarkdownComponents, createGuideUrlTransform } from './GuideMarkdown';
import { GuideItem, GuideSection } from './Guides';
import { GuideArticleStatus, GuidesStatus } from './HowToGuidesPresenter';

export type Props = {
    status: GuidesStatus;
    errorMessage: string;
    hero?: { title?: string; subtitle?: string };
    sections: Array<GuideSection>;
    thumbnailUrlById: Record<string, string>;
    selectedItem?: GuideItem | null;
    selectedSectionTitle?: string;
    articleStatus: GuideArticleStatus;
    articleMarkdown: string;
    onOpenGuide: (itemId: string) => void;
    onCloseGuide: () => void;
    onRetry: () => void;
};

// Keeps guide:<id> cross-references intact through the renderer's URL sanitiser.
const guideUrlTransform = createGuideUrlTransform(defaultUrlTransform);

export class HowToGuidesView extends Component<Props> {
    private scrollRef = React.createRef<HTMLDivElement>();

    componentDidUpdate(previousProps: Props) {
        // Moving between the list and a guide starts the new view at the top.
        if (previousProps.selectedItem?.id !== this.props.selectedItem?.id) {
            this.scrollRef.current?.scrollTo({ top: 0 });
        }
    }

    render() {
        const { status, selectedItem } = this.props;

        let content: React.ReactNode;
        if (status === 'loading') {
            content = this.renderMessage('Loading guides…');
        }
        else if (status === 'error') {
            content = this.renderError();
        }
        else if (selectedItem) {
            content = selectedItem.type === 'pdf' ? this.renderPdf(selectedItem) : this.renderArticle(selectedItem);
        }
        else {
            content = this.renderLibrary();
        }

        return <div ref={this.scrollRef} style={styles.page}>{content}</div>;
    }

    // ---------- Library ----------

    private renderLibrary(): React.ReactNode {
        const { hero, sections } = this.props;
        return (
            <div style={styles.libraryWrapper}>
                {hero && (
                    <div style={styles.hero}>
                        {hero.title && <h1 style={styles.heroTitle}>{hero.title}</h1>}
                        {hero.subtitle && <p style={styles.heroSubtitle}>{hero.subtitle}</p>}
                    </div>
                )}
                {sections.map((section) => this.renderSection(section))}
            </div>
        );
    }

    private renderSection(section: GuideSection): React.ReactNode {
        if (section.items.length === 0) {
            return null;
        }
        return (
            <section key={section.id} style={styles.section}>
                <h2 style={styles.sectionTitle}>{section.title}</h2>
                {section.description && <p style={styles.sectionDescription}>{section.description}</p>}
                <div style={styles.cardGrid}>
                    {section.items.map((item) => this.renderCard(item))}
                </div>
            </section>
        );
    }

    private renderCard(item: GuideItem): React.ReactNode {
        const thumbnailUrl = this.props.thumbnailUrlById[item.id];
        // A guide with nothing behind it yet is shown but not openable.
        const isOpenable = !item.comingSoon && (item.type === 'article' ? !!item.article : !!item.pdfUrl);

        return (
            <div
                key={item.id}
                role={isOpenable ? 'button' : undefined}
                tabIndex={isOpenable ? 0 : undefined}
                aria-disabled={!isOpenable}
                onClick={isOpenable ? () => this.props.onOpenGuide(item.id) : undefined}
                onKeyDown={isOpenable ? (event) => this.handleCardKeyDown(event, item.id) : undefined}
                style={{ ...styles.card, ...(isOpenable ? styles.cardOpenable : styles.cardDisabled) }}
            >
                <div style={styles.cardThumbnailFrame}>
                    {thumbnailUrl
                        ? <img src={thumbnailUrl} alt='' style={styles.cardThumbnail} />
                        : <div style={styles.cardThumbnailPlaceholder} aria-hidden='true' />}
                    {item.comingSoon && <span style={styles.comingSoonBadge}>Coming soon</span>}
                </div>
                <div style={styles.cardText}>
                    <div style={styles.cardTitle}>{item.title}</div>
                    {item.summary && <div style={styles.cardSummary}>{item.summary}</div>}
                    {item.type === 'pdf' && !item.comingSoon && <div style={styles.cardKind}>PDF</div>}
                </div>
            </div>
        );
    }

    private handleCardKeyDown(event: React.KeyboardEvent<HTMLDivElement>, itemId: string) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.props.onOpenGuide(itemId);
        }
    }

    // ---------- Article ----------

    private renderArticle(item: GuideItem): React.ReactNode {
        const { articleStatus, articleMarkdown } = this.props;
        return (
            <div style={styles.readerWrapper}>
                {this.renderBackButton()}
                {this.renderEyebrow()}
                <h1 style={styles.articleTitle}>{item.title}</h1>
                {articleStatus === 'loading' && this.renderMessage('Loading guide…')}
                {articleStatus === 'error' && this.renderMessage('This guide could not be loaded. Please try again.')}
                {articleStatus === 'ready' && (
                    <div className='guide-article' style={styles.articleBody}>
                        <ReactMarkdown
                            urlTransform={guideUrlTransform}
                            components={createGuideMarkdownComponents(this.props.onOpenGuide)}
                        >
                            {articleMarkdown}
                        </ReactMarkdown>
                    </div>
                )}
            </div>
        );
    }

    // ---------- PDF ----------

    private renderPdf(item: GuideItem): React.ReactNode {
        return (
            <div style={styles.readerWrapper}>
                {this.renderBackButton()}
                {this.renderEyebrow()}
                <h1 style={styles.pdfTitle}>{item.title}</h1>
                {item.pdfUrl && (
                    <p style={styles.pdfLinkRow}>
                        <a href={item.pdfUrl} target='_blank' rel='noreferrer' style={styles.link}>
                            Open in a new tab
                        </a>
                    </p>
                )}
                {item.pdfUrl
                    ? <iframe src={item.pdfUrl} title={item.title} style={styles.pdfFrame} />
                    : this.renderMessage('This document is not available yet.')}
            </div>
        );
    }

    // ---------- Shared ----------

    private renderEyebrow(): React.ReactNode {
        const { selectedSectionTitle } = this.props;
        return selectedSectionTitle ? <p style={styles.eyebrow}>{selectedSectionTitle}</p> : null;
    }

    private renderBackButton(): React.ReactNode {
        return (
            <button type='button' onClick={this.props.onCloseGuide} style={styles.backButton}>
                ← All guides
            </button>
        );
    }

    private renderMessage(message: string): React.ReactNode {
        return <div style={styles.message}>{message}</div>;
    }

    private renderError(): React.ReactNode {
        return (
            <div style={styles.message}>
                <p style={styles.articleParagraph}>The guides could not be loaded.</p>
                {this.props.errorMessage && <p style={styles.errorDetail}>{this.props.errorMessage}</p>}
                <button type='button' onClick={this.props.onRetry} style={styles.retryButton}>
                    Try again
                </button>
            </div>
        );
    }
}

const styles: { [key: string]: React.CSSProperties } = {
    page: {
        width: '100%',
        height: 'calc(100vh - 140px)',
        backgroundColor: '#f7f7ff',
        overflowY: 'auto',
    },
    libraryWrapper: {
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '24px 24px 64px',
    },
    hero: {
        backgroundColor: '#eceafc',
        borderRadius: '16px',
        padding: '28px 32px',
        marginBottom: '32px',
    },
    heroTitle: {
        margin: 0,
        fontSize: '28px',
        fontWeight: 600,
        color: '#18143d',
    },
    heroSubtitle: {
        margin: '8px 0 0',
        fontSize: '15px',
        lineHeight: 1.5,
        color: '#4a4766',
    },
    section: {
        marginBottom: '40px',
    },
    sectionTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: 600,
        color: '#18143d',
    },
    sectionDescription: {
        margin: '6px 0 18px',
        fontSize: '14px',
        color: '#686577',
    },
    cardGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '20px',
    },
    card: {
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    cardOpenable: {
        cursor: 'pointer',
    },
    cardDisabled: {
        cursor: 'default',
        opacity: 0.75,
    },
    cardThumbnailFrame: {
        position: 'relative',
        width: '100%',
        aspectRatio: '646 / 308',
        backgroundColor: '#f2f2f7',
    },
    cardThumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    cardThumbnailPlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: '#ececf4',
    },
    comingSoonBadge: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: '#18143d',
        color: '#ffffff',
        fontSize: '11px',
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: '999px',
    },
    cardText: {
        padding: '14px 16px 18px',
    },
    cardTitle: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#18143d',
        lineHeight: 1.35,
    },
    cardSummary: {
        marginTop: '6px',
        fontSize: '13px',
        lineHeight: 1.5,
        color: '#686577',
    },
    cardKind: {
        marginTop: '10px',
        display: 'inline-block',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: '#544ac2',
        backgroundColor: '#eceafc',
        padding: '3px 8px',
        borderRadius: '4px',
    },
    readerWrapper: {
        maxWidth: '760px',
        margin: '0 auto',
        padding: '24px 24px 64px',
    },
    backButton: {
        background: 'none',
        border: 'none',
        padding: 0,
        marginBottom: '20px',
        color: '#544ac2',
        fontSize: '14px',
        cursor: 'pointer',
    },
    eyebrow: {
        margin: '0 0 12px',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#544ac2',
    },
    articleTitle: {
        margin: '0 0 24px',
        fontSize: '32px',
        fontWeight: 600,
        lineHeight: 1.2,
        color: '#18143d',
    },
    articleBody: {
        fontSize: '15px',
        lineHeight: 1.6,
        color: '#2b2842',
    },
    articleParagraph: {
        margin: '0 0 18px',
    },
    link: {
        color: '#544ac2',
        textDecoration: 'underline',
    },
    pdfTitle: {
        // Sits directly above its link, so it carries less space beneath than an
        // article title does.
        margin: '0 0 12px',
        fontSize: '32px',
        fontWeight: 600,
        lineHeight: 1.2,
        color: '#18143d',
    },
    pdfLinkRow: {
        margin: '0 0 20px',
        fontSize: '15px',
    },
    pdfFrame: {
        width: '100%',
        height: 'calc(100vh - 320px)',
        minHeight: '480px',
        border: Theme.Colours.border,
        borderRadius: '12px',
        backgroundColor: Theme.Colours.workArea,
    },
    message: {
        maxWidth: '760px',
        margin: '0 auto',
        padding: '40px 24px',
        fontSize: '14px',
        color: '#686577',
    },
    errorDetail: {
        margin: '0 0 18px',
        fontSize: '13px',
        color: '#9a97a8',
    },
    retryButton: {
        padding: '8px 16px',
        borderRadius: '6px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.buttonBackground,
        color: Theme.Colours.buttonText,
        fontSize: '14px',
        cursor: 'pointer',
    },
};
